import React, { useState, useEffect } from 'react';
import { observer } from 'mobx-react-lite';
import { useTranslation } from 'react-i18next';
import {
  Card,
  Row,
  Col,
  Statistic,
  Table,
  DatePicker,
  Select,
  Button,
  Spin,
  Alert,
  Space,
  Typography,
  Tag,
  Divider
} from 'antd';
import {
  EuroOutlined,
  ShoppingCartOutlined,
  PercentageOutlined,
  BankOutlined,
  HeartOutlined,
  CalendarOutlined,
  ReloadOutlined
} from '@ant-design/icons';
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  BarChart,
  Bar
} from 'recharts';
import orderService from '../../../services/orderService';
import BackButton from '../../../components/BackButton';
import dayjs from 'dayjs';

const { Title, Text } = Typography;
const { RangePicker } = DatePicker;
const { Option } = Select;

const RevenueReport = observer(() => {
  const { t } = useTranslation();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [reportData, setReportData] = useState(null);
  const [period, setPeriod] = useState('all');
  const [dateRange, setDateRange] = useState([null, null]);

  useEffect(() => {
    fetchRevenueReport();
  }, [period]);

  const fetchRevenueReport = async () => {
    setLoading(true);
    setError(null);

    const params = { period };
    if (dateRange[0] && dateRange[1]) {
      params.start_date = dateRange[0].format('YYYY-MM-DD');
      params.end_date = dateRange[1].format('YYYY-MM-DD');
    }

    const result = await orderService.getRevenueReport(params);

    if (result.success) {
      setReportData(result.data);
    } else {
      setError(result.error);
    }
    setLoading(false);
  };

  const handleDateRangeChange = (dates) => {
    setDateRange(dates || [null, null]);
  };

  const handleApplyDateFilter = () => {
    fetchRevenueReport();
  };

  const formatCurrency = (amount) => {
    const num = parseFloat(amount) || 0;
    return new Intl.NumberFormat('el-GR', {
      style: 'currency',
      currency: 'EUR'
    }).format(num);
  };

  const statusColors = {
    CONFIRMED: 'blue',
    PROCESSING: 'orange',
    SHIPPED: 'cyan',
    DELIVERED: 'green'
  };

  const getStatusText = (status) => {
    const statusMap = {
      CONFIRMED: t('partner.orders.status.confirmed'),
      PROCESSING: t('partner.orders.status.processing'),
      SHIPPED: t('partner.orders.status.shipped'),
      DELIVERED: t('partner.orders.status.delivered')
    };
    return statusMap[status] || status;
  };

  const statusBreakdownColumns = [
    {
      title: t('common.status'),
      dataIndex: 'status',
      key: 'status',
      render: (status) => (
        <Tag color={statusColors[status]}>{getStatusText(status)}</Tag>
      )
    },
    {
      title: t('partner.reports.orders'),
      dataIndex: 'count',
      key: 'count'
    },
    {
      title: t('partner.reports.totalAmount'),
      dataIndex: 'total',
      key: 'total',
      render: (total) => formatCurrency(total)
    }
  ];

  if (loading && !reportData) {
    return (
      <div style={{ padding: 24, textAlign: 'center' }}>
        <Spin size="large" />
        <Text style={{ display: 'block', marginTop: 16 }}>{t('partner.reports.loading')}</Text>
      </div>
    );
  }

  if (error && !reportData) {
    return (
      <div style={{ padding: 24 }}>
        <Alert
          message={t('common.error')}
          description={error}
          type="error"
          showIcon
        />
      </div>
    );
  }

  const { summary, partner, status_breakdown, monthly_breakdown } = reportData || {};

  return (
    <div style={{ padding: 24 }}>
      <BackButton to="/partner/dashboard" label={t('partner.reports.backToDashboard')} />

      <div style={{ marginBottom: 24 }}>
        <Title level={2}>
          <EuroOutlined /> {t('partner.reports.title')}
        </Title>
        <Text type="secondary">
          {t('partner.reports.description', { store: partner?.store_name })}
        </Text>
      </div>

      {/* Filters */}
      <Card style={{ marginBottom: 24 }}>
        <Space wrap>
          <Select
            value={period}
            onChange={setPeriod}
            style={{ width: 150 }}
          >
            <Option value="all">{t('partner.reports.allTime')}</Option>
            <Option value="month">{t('partner.reports.last30Days')}</Option>
            <Option value="year">{t('partner.reports.lastYear')}</Option>
          </Select>
          <RangePicker
            value={dateRange}
            onChange={handleDateRangeChange}
          />
          <Button
            type="primary"
            icon={<CalendarOutlined />}
            onClick={handleApplyDateFilter}
          >
            {t('partner.reports.applyFilter')}
          </Button>
          <Button
            icon={<ReloadOutlined />}
            onClick={fetchRevenueReport}
            loading={loading}
          >
            {t('common.refresh')}
          </Button>
        </Space>
      </Card>

      {/* Summary Cards */}
      <Row gutter={[16, 16]} style={{ marginBottom: 24 }}>
        <Col xs={24} sm={12} md={6}>
          <Card>
            <Statistic
              title={t('partner.reports.totalSales')}
              value={formatCurrency(summary?.total_sales)}
              prefix={<ShoppingCartOutlined />}
              valueStyle={{ color: '#3f8600' }}
            />
          </Card>
        </Col>
        <Col xs={24} sm={12} md={6}>
          <Card>
            <Statistic
              title={t('partner.reports.platformFees')}
              value={formatCurrency(summary?.platform_fees)}
              prefix={<PercentageOutlined />}
              valueStyle={{ color: '#cf1322' }}
              suffix={
                <Text type="secondary" style={{ fontSize: 12 }}>
                  ({partner?.fee_percentage}%)
                </Text>
              }
            />
          </Card>
        </Col>
        <Col xs={24} sm={12} md={6}>
          <Card>
            <Statistic
              title={t('partner.reports.giveBackProgram')}
              value={formatCurrency(summary?.give_back_amount)}
              prefix={<HeartOutlined />}
              valueStyle={{ color: '#eb2f96' }}
            />
          </Card>
        </Col>
        <Col xs={24} sm={12} md={6}>
          <Card>
            <Statistic
              title={t('partner.reports.balanceDue')}
              value={formatCurrency(summary?.balance_due)}
              prefix={<BankOutlined />}
              valueStyle={{ color: '#1890ff' }}
            />
            <Text type="secondary" style={{ fontSize: 12 }}>
              {t('partner.reports.amountToBeTransferred')}
            </Text>
          </Card>
        </Col>
      </Row>

      {/* Additional Stats */}
      <Row gutter={[16, 16]} style={{ marginBottom: 24 }}>
        <Col xs={24} sm={12} md={8}>
          <Card>
            <Statistic
              title={t('partner.reports.totalOrders')}
              value={summary?.order_count}
              prefix={<ShoppingCartOutlined />}
            />
          </Card>
        </Col>
        <Col xs={24} sm={12} md={8}>
          <Card>
            <Statistic
              title={t('partner.reports.avgOrderValue')}
              value={formatCurrency(summary?.avg_order_value)}
              prefix={<EuroOutlined />}
            />
          </Card>
        </Col>
        <Col xs={24} sm={12} md={8}>
          <Card>
            <Statistic
              title={t('partner.reports.totalShipping')}
              value={formatCurrency(summary?.total_shipping)}
              prefix={<ShoppingCartOutlined />}
            />
          </Card>
        </Col>
      </Row>

      <Row gutter={[16, 16]}>
        {/* Monthly Trend Chart */}
        <Col xs={24} lg={16}>
          <Card title={t('partner.reports.monthlyTrend')}>
            {monthly_breakdown && monthly_breakdown.length > 0 ? (
              <ResponsiveContainer width="100%" height={300}>
                <LineChart
                  data={[...monthly_breakdown].reverse()}
                  margin={{ top: 5, right: 30, left: 20, bottom: 5 }}
                >
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis dataKey="month" />
                  <YAxis />
                  <Tooltip
                    formatter={(value) => formatCurrency(value)}
                  />
                  <Line
                    type="monotone"
                    dataKey="total_sales"
                    stroke="#52c41a"
                    strokeWidth={2}
                    name={t('partner.reports.sales')}
                  />
                  <Line
                    type="monotone"
                    dataKey="total_fees"
                    stroke="#f5222d"
                    strokeWidth={2}
                    name={t('partner.reports.fees')}
                  />
                </LineChart>
              </ResponsiveContainer>
            ) : (
              <div style={{ textAlign: 'center', padding: 40 }}>
                <Text type="secondary">{t('partner.reports.noChartData')}</Text>
              </div>
            )}
          </Card>
        </Col>

        {/* Status Breakdown */}
        <Col xs={24} lg={8}>
          <Card title={t('partner.reports.ordersByStatus')}>
            <Table
              dataSource={status_breakdown}
              columns={statusBreakdownColumns}
              rowKey="status"
              pagination={false}
              size="small"
            />
          </Card>
        </Col>
      </Row>

      <Divider />

      {/* Monthly Breakdown Table */}
      <Card title={t('partner.reports.monthlyBreakdown')}>
        <Table
          dataSource={monthly_breakdown}
          rowKey="month"
          pagination={{ pageSize: 6 }}
          columns={[
            {
              title: t('partner.reports.month'),
              dataIndex: 'month',
              key: 'month',
              render: (month) => month ? dayjs(month).format('MMMM YYYY') : '-'
            },
            {
              title: t('partner.reports.orders'),
              dataIndex: 'count',
              key: 'count'
            },
            {
              title: t('partner.reports.totalSales'),
              dataIndex: 'total_sales',
              key: 'total_sales',
              render: (val) => formatCurrency(val)
            },
            {
              title: t('partner.reports.platformFees'),
              dataIndex: 'total_fees',
              key: 'total_fees',
              render: (val) => <Text type="danger">{formatCurrency(val)}</Text>
            },
            {
              title: t('partner.reports.shipping'),
              dataIndex: 'total_shipping',
              key: 'total_shipping',
              render: (val) => formatCurrency(val)
            },
            {
              title: t('partner.reports.netEarnings'),
              key: 'net',
              render: (_, record) => {
                const net = parseFloat(record.total_sales || 0) - parseFloat(record.total_fees || 0);
                return <Text strong style={{ color: '#52c41a' }}>{formatCurrency(net)}</Text>;
              }
            }
          ]}
        />
      </Card>
    </div>
  );
});

export default RevenueReport;
